import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY = 'harbournest_access_token';
const API_URL = process.env.REACT_APP_API_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refresh token and get new access token
  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const { accessToken, user: userData } = await response.json();
        localStorage.setItem(TOKEN_KEY, accessToken);
        setUser(userData);
        return true;
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
    return false;
  }, []);

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          } else if (response.status === 401) {
            // Token expired, try refresh
            const refreshed = await refreshToken();
            if (!refreshed) {
              localStorage.removeItem(TOKEN_KEY);
            }
          }
        } catch (err) {
          console.error('Auth init error:', err);
          localStorage.removeItem(TOKEN_KEY);
        }
      } else {
        // No token in storage, try refresh (might have valid cookie)
        await refreshToken();
      }

      setLoading(false);
    };

    initAuth();
  }, [refreshToken]);

  // Start Google OAuth flow
  const loginWithGoogle = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      window.location.href = data.authUrl;
    } catch (err) {
      setError('Failed to start Google login');
      console.error('Google login error:', err);
    }
  }, []);

  // Start Apple OAuth flow
  const loginWithApple = useCallback(async () => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/apple`, { method: 'POST' });
      const data = await response.json();

      if (data.error) {
        setError(data.error);
        return;
      }

      window.location.href = data.authUrl;
    } catch (err) {
      setError('Failed to start Apple login');
      console.error('Apple login error:', err);
    }
  }, []);

  // Login with email and password
  const loginWithEmail = useCallback(async (email, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error);
        return { success: false, error: data.error, provider: data.provider };
      }

      localStorage.setItem(TOKEN_KEY, data.accessToken);
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      setError('Login failed');
      console.error('Email login error:', err);
      return { success: false, error: 'Login failed' };
    }
  }, []);

  // Register with email and password
  const registerWithEmail = useCallback(async (email, password, name) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error);
        return { success: false, error: data.error };
      }

      localStorage.setItem(TOKEN_KEY, data.accessToken);
      setUser(data.user);
      return { success: true, user: data.user, isNewUser: data.isNewUser };
    } catch (err) {
      setError('Registration failed');
      console.error('Registration error:', err);
      return { success: false, error: 'Registration failed' };
    }
  }, []);

  // Resend email verification
  const resendVerification = useCallback(async (email) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      return { success: true, message: data.message };
    } catch (err) {
      return { success: false, error: 'Failed to resend verification email' };
    }
  }, []);

  // Request password reset
  const forgotPassword = useCallback(async (email) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      return { success: true, message: data.message, resetLink: data.resetLink };
    } catch (err) {
      setError('Failed to send reset email');
      return { success: false, error: 'Failed to send reset email' };
    }
  }, []);

  // Reset password with token
  const resetPassword = useCallback(async (token, password) => {
    try {
      setError(null);
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error);
        return { success: false, error: data.error };
      }

      return { success: true, message: data.message };
    } catch (err) {
      setError('Password reset failed');
      return { success: false, error: 'Password reset failed' };
    }
  }, []);

  // Handle OAuth callback (receives token from URL)
  const handleAuthCallback = useCallback(async (token) => {
    localStorage.setItem(TOKEN_KEY, token);

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
    } catch (err) {
      console.error('Auth callback error:', err);
    }

    throw new Error('Failed to get user data');
  }, []);

  // Complete onboarding
  const completeOnboarding = useCallback(async (data) => {
    const token = localStorage.getItem(TOKEN_KEY);

    try {
      const response = await fetch(`${API_URL}/api/auth/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser);
        return updatedUser;
      }
    } catch (err) {
      console.error('Onboarding error:', err);
    }

    throw new Error('Onboarding failed');
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  // Get access token for API calls
  const getToken = useCallback(() => {
    return localStorage.getItem(TOKEN_KEY);
  }, []);

  // Fetch wrapper that auto-refreshes token on 401
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const headers = {
      ...options.headers,
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    let response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        const newToken = localStorage.getItem(TOKEN_KEY);
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    return response;
  }, [refreshToken]);

  // Check if user has permission for a property
  const hasPermission = useCallback((permission, propertyId) => {
    if (!user?.properties) return false;

    const access = user.properties.find(p => p.propertyId === propertyId);
    return access?.permissions?.includes(permission) || false;
  }, [user]);

  // Get user's tier for a property
  const getUserTier = useCallback((propertyId) => {
    if (!user?.properties) return null;

    const access = user.properties.find(p => p.propertyId === propertyId);
    if (!access) return null;

    if (access.role === 'owner') return 'primary';
    if (access.role === 'family_member') return 'secondary';
    return 'tertiary';
  }, [user]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    registerWithEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    handleAuthCallback,
    logout,
    completeOnboarding,
    refreshToken,
    getToken,
    authFetch,
    hasPermission,
    getUserTier,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
